import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import FieldSelectionPanel from './components/FieldSelectionPanel';
import ExportConfiguration from './components/ExportConfiguration';
import SelectionSummary from './components/SelectionSummary';
import Icon from '../../components/AppIcon';
import { useNotification } from '../../components/ui/NotificationProvider';

const DatabaseFieldSelection = () => {
  const { showWarning, showError, showSuccess, showInfo } = useNotification();
  const navigate = useNavigate();

  // Available database fields based on the user's attachment
  const [availableFields] = useState({
    productInfo: [
      { id: 'id', name: 'Id', type: 'number', required: true, description: 'Identificador único del producto' },
      { id: 'userProductId', name: 'User Product Id', type: 'string', required: false, description: 'ID del producto asignado por el usuario' },
      { id: 'familyId', name: 'Family Id', type: 'string', required: false, description: 'Identificador de la familia de productos' }
    ],
    catalogDetails: [
      { id: 'situacionCatalogo', name: 'Situación Catálogo', type: 'string', required: false, description: 'Estado actual en el catálogo' },
      { id: 'vendedor', name: 'Vendedor', type: 'string', required: false, description: 'Información del vendedor' },
      { id: 'tiendaOficial', name: 'Tienda Oficial', type: 'boolean', required: false, description: 'Marca de tienda oficial' }
    ],
    contentFields: [
      { id: 'categoria', name: 'Categoría', type: 'string', required: true, description: 'Categoría del producto en MercadoLibre' },
      { id: 'titulo', name: 'Título', type: 'string', required: true, description: 'Título del producto' },
      { id: 'descripcion', name: 'Descripción', type: 'text', required: true, description: 'Descripción detallada del producto' }
    ],
    pricingData: [
      { id: 'precio', name: 'Precio', type: 'number', required: true, description: 'Precio principal del producto' },
      { id: 'precioDetalle', name: 'Precio Detalle', type: 'number', required: false, description: 'Precio detallado con descuentos' },
      { id: 'precioAutomatizado', name: 'Precio Automatizado', type: 'number', required: false, description: 'Precio calculado automáticamente' },
      { id: 'precioMayorista', name: 'Precio Mayorista', type: 'number', required: false, description: 'Precio para mayoristas' },
      { id: 'moneda', name: 'Moneda', type: 'string', required: true, description: 'Moneda del precio' },
      { id: 'descuentoIndividual', name: 'Descuento Individual', type: 'number', required: false, description: 'Descuento específico del producto' }
    ],
    inventoryManagement: [
      { id: 'estado', name: 'Estado', type: 'string', required: true, description: 'Estado del producto (activo/inactivo)' },
      { id: 'stock', name: 'Stock', type: 'number', required: true, description: 'Cantidad disponible' },
      { id: 'disponibilidadStock', name: 'Disponibilidad de stock', type: 'string', required: false, description: 'Estado de disponibilidad' },
      { id: 'cantidadMaxima', name: 'Cantidad Máxima', type: 'number', required: false, description: 'Cantidad máxima por compra' },
      { id: 'imagenes', name: 'Imágenes', type: 'array', required: false, description: 'URLs de imágenes del producto' },
      { id: 'video', name: 'Video', type: 'string', required: false, description: 'URL del video del producto' }
    ],
    publishingSettings: [
      { id: 'tipoPublicacion', name: 'Tipo de Publicación', type: 'string', required: true, description: 'Tipo de publicación en MercadoLibre' },
      { id: 'msns', name: 'MSNs', type: 'array', required: false, description: 'Números de serie del producto' },
      { id: 'cuotas', name: 'Cuotas', type: 'string', required: false, description: 'Opciones de financiamiento' },
      { id: 'visitas', name: 'Visitas', type: 'number', required: false, description: 'Número de visitas del producto' },
      { id: 'comision', name: 'Comisión', type: 'number', required: false, description: 'Comisión de MercadoLibre' },
      { id: 'vendidos', name: 'Vendidos', type: 'number', required: false, description: 'Cantidad vendida' }
    ],
    additionalData: [
      { id: 'datosEnvio', name: 'Datos de Envío', type: 'object', required: false, description: 'Información de envío' },
      { id: 'tags', name: 'Tags', type: 'array', required: false, description: 'Etiquetas del producto' },
      { id: 'garantia', name: 'Garantía', type: 'string', required: false, description: 'Información de garantía' },
      { id: 'urlPublicacion', name: 'URL Publicación', type: 'string', required: false, description: 'URL de la publicación' },
      { id: 'fechaCreacion', name: 'Fecha Creación', type: 'datetime', required: false, description: 'Fecha de creación del producto' },
      { id: 'calidad', name: 'Calidad', type: 'string', required: false, description: 'Calidad del producto' },
      { id: 'ultimaActualizacion', name: 'Última Actualización', type: 'datetime', required: false, description: 'Fecha de última modificación' },
      { id: 'campana', name: 'Campaña', type: 'string', required: false, description: 'Campaña de marketing asociada' }
    ]
  });

  // Selection state
  const [selectedFields, setSelectedFields] = useState(new Set([
    'id', 'userProductId', 'tiendaOficial', 'titulo', 'descripcion', 'precio', 
    'precioAutomatizado', 'estado', 'stock', 'tipoPublicacion', 'datosEnvio',
    'fechaCreacion', 'ultimaActualizacion'
  ]));

  // Export configuration
  const [exportConfig, setExportConfig] = useState({
    rowLimit: 1000,
    format: 'excel',
    includeHeaders: true,
    filename: 'productos_mercadolibre'
  });

  // UI state
  const [isGenerating, setIsGenerating] = useState(false);
  const [fieldCount, setFieldCount] = useState(0);
  const [estimatedSize, setEstimatedSize] = useState('0 KB');

  // Calculate field count and estimated size
  useEffect(() => {
    const count = selectedFields?.size;
    const estimatedKB = Math.round(count * exportConfig?.rowLimit * 0.1);
    
    setFieldCount(count);
    setEstimatedSize(estimatedKB > 1024 ? `${(estimatedKB / 1024)?.toFixed(1)} MB` : `${estimatedKB} KB`);
  }, [selectedFields, exportConfig]);

  // Handle field selection
  const handleFieldToggle = (fieldId) => {
    const newSelection = new Set(selectedFields);
    if (newSelection?.has(fieldId)) {
      newSelection?.delete(fieldId);
    } else {
      newSelection?.add(fieldId);
    }
    setSelectedFields(newSelection);
  };

  // Handle category selection
  const handleCategoryToggle = (categoryFields, selectAll) => {
    const newSelection = new Set(selectedFields);
    
    categoryFields?.forEach(field => {
      if (selectAll) {
        newSelection?.add(field?.id);
      } else {
        newSelection?.delete(field?.id);
      }
    });
    
    setSelectedFields(newSelection);
  };

  // Handle export configuration changes
  const handleConfigChange = (key, value) => {
    setExportConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Generate Excel file with enhanced functionality
  const handleGenerateExport = async () => {
    if (selectedFields?.size === 0) {
      showWarning?.('Selecciona al menos un campo para exportar', { title: 'Campos requeridos' });
      return;
    }

    setIsGenerating(true);

    try {
      // Save selected fields to localStorage for bulk upload integration
      const selectedFieldsArray = Array.from(selectedFields);
      localStorage.setItem('awsSelectedFields', JSON.stringify(selectedFieldsArray));
      
      // Simulate data generation and export
      await new Promise(resolve => setTimeout(resolve, 2000));

      // In a real implementation, this would generate the actual Excel file
      const mockData = generateMockDataWithSelectedColumns();
      downloadExcelFile(mockData);

      // Navigate to bulk upload with selected columns information
      navigate('/bulk-upload', {
        state: {
          message: 'Archivo Excel generado correctamente con columnas seleccionadas',
          fieldsSelected: selectedFields?.size,
          recordsGenerated: Math.min(exportConfig?.rowLimit, 500),
          awsColumnsSelected: selectedFieldsArray,
          enhancementsApplied: [
            'Disponibilidad configurada a 12 días',
            'Precios optimizados para terminar en 999',
            'Categorías verificadas automáticamente',
            'Ajuste +40% en edición masiva'
          ]
        }
      });
    } catch (error) {
      console.error('Error generating export:', error);
      showError?.('Error al generar el archivo. Inténtalo de nuevo.', { title: 'Error de exportación', persistent: false });
    } finally {
      setIsGenerating(false);
    }
  };

  // Enhanced mock data generation with selected columns
  const generateMockDataWithSelectedColumns = () => {
    return Array.from({ length: Math.min(exportConfig?.rowLimit, 100) }, (_, index) => {
      const mockProduct = {};
      
      selectedFields?.forEach(fieldId => {
        switch (fieldId) {
          case 'id':
            mockProduct[fieldId] = index + 1;
            break;
          case 'titulo':
            mockProduct[fieldId] = `Producto ${index + 1}`;
            break;
          case 'precio':
            // Generate price ending in 999
            const basePrice = Math.floor(Math.random() * 10000) + 1000;
            mockProduct[fieldId] = Math.floor(basePrice / 1000) * 1000 + 999;
            break;
          case 'stock':
            mockProduct[fieldId] = Math.max(Math.floor(Math.random() * 100) + 1, 12);
            break;
          case 'disponibilidadStock':
            mockProduct[fieldId] = '12 días';
            break;
          case 'categoria':
            const categories = ['MLA1051', 'MLA1648', 'MLA1367', 'MLA1132'];
            mockProduct[fieldId] = categories?.[Math.floor(Math.random() * categories?.length)];
            break;
          case 'estado':
            mockProduct[fieldId] = 'active';
            break;
          default:
            mockProduct[fieldId] = `Valor ${index + 1}`;
        }
      });
      
      return mockProduct;
    });
  };

  // Download Excel file (mock implementation)
  const downloadExcelFile = (data) => {
    // This would use the xlsx library to generate and download the file
    console.log('Downloading Excel file with data:', data);
    
    // Mock download
    const blob = new Blob(['Mock Excel Data'], { type: 'application/vnd.ms-excel' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportConfig?.filename}.xlsx`;
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  // Check if user came from AWS connection
  useEffect(() => {
    const savedConnection = localStorage.getItem('awsConnection');
    if (!savedConnection) {
      navigate('/aws-database-connection');
    }
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>Selección de Campos AWS - MercadoLibre Bulk Publisher</title>
        <meta name="description" content="Selecciona campos específicos de tu base de datos AWS para exportar con optimizaciones automáticas" />
      </Helmet>

      <div className="min-h-screen bg-background">
        
        <main className="pt-10 pb-8">
          <div className="max-w-7xl mx-auto px-6">
            {/* Header Section */}
            <div className="mb-8">
              <div className="flex items-center space-x-4 mb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/aws-database-connection')}
                  className="text-text-secondary hover:text-text-primary"
                >
                  <Icon name="ArrowLeft" size={16} className="mr-1" />
                  Volver a Conexión
                </Button>
              </div>

              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Table" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-text-primary">
                    Selección de Campos AWS
                  </h1>
                  <p className="text-text-secondary mt-1">
                    Elige los campos específicos con optimizaciones automáticas para MercadoLibre
                  </p>
                </div>
              </div>

              {/* Enhanced features notice */}
              <div className="mb-6 p-4 bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="Zap" className="text-primary mt-1" size={20} />
                  <div>
                    <h3 className="font-semibold text-text-primary mb-2">Mejoras Automáticas Incluidas</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-text-secondary">
                      <div className="flex items-center space-x-2">
                        <Icon name="Clock" size={14} className="text-success" />
                        <span>Disponibilidad automática: 12 días</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="TrendingUp" size={14} className="text-success" />
                        <span>Precios optimizados terminados en 999</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="Tag" size={14} className="text-success" />
                        <span>Categorías verificadas automáticamente</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="Calculator" size={14} className="text-success" />
                        <span>Ajuste +40% en edición masiva</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Selection Summary */}
              <SelectionSummary 
                selectedCount={fieldCount}
                totalFields={Object.values(availableFields)?.flat()?.length}
                estimatedSize={estimatedSize}
                rowLimit={exportConfig?.rowLimit}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Field Selection Panel */}
              <div className="lg:col-span-3">
                <FieldSelectionPanel
                  availableFields={availableFields}
                  selectedFields={selectedFields}
                  onFieldToggle={handleFieldToggle}
                  onCategoryToggle={handleCategoryToggle}
                />
              </div>

              {/* Export Configuration Sidebar */}
              <div className="lg:col-span-1">
                <ExportConfiguration
                  config={exportConfig}
                  onConfigChange={handleConfigChange}
                  selectedCount={fieldCount}
                  onGenerate={handleGenerateExport}
                  isGenerating={isGenerating}
                />
              </div>
            </div>

            {/* Mobile Generate Button */}
            <div className="lg:hidden mt-8">
              <Button
                onClick={handleGenerateExport}
                disabled={isGenerating || selectedFields?.size === 0}
                className="w-full"
                size="lg"
              >
                <Icon name="Download" size={20} className="mr-2" />
                {isGenerating ? 'Generando...' : 'Generar Archivo Excel'}
              </Button>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default DatabaseFieldSelection;